import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, BRAND } from "../App";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-black text-white pt-32 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#E31E24] mb-4">Contact</p>
          <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight">Get In Touch</h1>
          <p className="text-white/80 mt-3">We're here to help. Reach out anytime.</p>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl font-black uppercase mb-6">Contact Information</h2>
              <p className="text-black/70 mb-8">
                Call or text {BRAND.owner} directly for the fastest response. For general inquiries, use the form or send us an email.
              </p>

              <div className="space-y-4">
                <a href={BRAND.phoneHref} className="group flex items-center gap-4 p-6 bg-white border border-black/10 hover:border-[#E31E24] transition-all">
                  <div className="w-14 h-14 bg-[#E31E24] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-black/60">Call or Text {BRAND.owner}</p>
                    <p className="text-xl font-black text-black group-hover:text-[#E31E24] transition-colors">{BRAND.phone}</p>
                  </div>
                </a>

                <a href={BRAND.emailHref} className="group flex items-center gap-4 p-6 bg-white border border-black/10 hover:border-[#E31E24] transition-all">
                  <div className="w-14 h-14 bg-[#E31E24] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wide text-black/60">Email Us</p>
                    <p className="text-sm sm:text-base font-black text-black group-hover:text-[#E31E24] transition-colors break-all">{BRAND.email}</p>
                  </div>
                </a>

                <a href={`sms:${BRAND.phoneHref.replace('tel:', '')}`} className="group flex items-center gap-4 p-6 bg-white border border-black/10 hover:border-[#E31E24] transition-all">
                  <div className="w-14 h-14 bg-[#E31E24] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <MessageSquare size={24} className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-black/60">Send a Text</p>
                    <p className="text-xl font-black text-black group-hover:text-[#E31E24] transition-colors">{BRAND.phone}</p>
                  </div>
                </a>
              </div>

              <div className="mt-8 p-6 bg-black text-white">
                <p className="text-sm uppercase tracking-wide text-white/60 mb-2">Business Hours</p>
                <p className="font-bold">Monday - Saturday: 8:00 AM - 8:00 PM</p>
                <p className="font-bold">Sunday: By appointment</p>
                <p className="text-white/60 text-sm mt-2">After-hours transport available upon request.</p>
              </div>
            </motion.div>

            <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-white p-8 border border-black/10">
              <h3 className="text-2xl font-black uppercase mb-6">Send a Message</h3>
              <div className="space-y-4">
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="Your Name *" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                <input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  placeholder="Email Address *" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                  placeholder="Phone Number" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                <input type="text" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})}
                  placeholder="Subject" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none" />
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})}
                  placeholder="Your Message *" className="w-full border border-black/20 p-3 focus:border-[#E31E24] focus:outline-none resize-none" />
                <button type="submit" disabled={submitting}
                  className="w-full bg-[#E31E24] text-white font-bold uppercase py-4 hover:bg-[#C01A1F] disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting ? "Sending..." : "Send Message"}
                  <Send size={18} />
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
